import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, timeAgo } from "../lib/api";
import { StoryCard, LeadHero, SixtySecondsRail } from "../components/StoryUI";
import { useApp } from "../lib/ctx";
import { Instagram, Facebook, Linkedin, Twitter, Youtube, Mail, TrendingUp, TrendingDown } from "lucide-react";

const Section = ({ title, children, kicker, link }) => (
  <section className="mt-10">
    <div className="flex items-end justify-between px-4 sm:px-6 mb-4">
      <div>
        {kicker && <div className="text-[11px] font-black uppercase tracking-widest text-signal">{kicker}</div>}
        <h2 className="font-display font-extrabold text-xl sm:text-2xl">{title}</h2>
      </div>
      {link && <Link to={link} className="text-xs font-bold text-signal hover:underline">See more →</Link>}
    </div>
    {children}
  </section>
);

const MarketWidget = ({ market }) => {
  if (!market?.dsex) return null;
  const row = (label, value) => {
    const up = /▲|\+/.test(value);
    return (
      <div className="flex items-center justify-between py-2 border-b last:border-0 border-border" key={label}>
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
        <span className={`text-sm font-bold inline-flex items-center gap-1 ${up ? "text-emerald-600" : "text-signal"}`}>
          {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {value}
        </span>
      </div>
    );
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-4 card-soft" data-testid="market-widget">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-extrabold text-lg">Markets</h3>
        <span className="text-[10px] text-muted-foreground font-medium">Updated {timeAgo(market.updated_at)}</span>
      </div>
      {row("DSEX", market.dsex)}
      {row("USD/BDT", market.usd_bdt)}
      {row("Gold", market.gold)}
    </div>
  );
};

const ScoresWidget = ({ scores }) => (
  <div className="rounded-2xl border border-border bg-card p-4 card-soft" data-testid="scores-widget">
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-display font-extrabold text-lg">Live scores</h3>
      <span className="text-[10px] text-signal font-black uppercase tracking-widest inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />LIVE
      </span>
    </div>
    <div className="space-y-3">
      {scores?.map(s => (
        <div key={s.id} className="rounded-xl bg-muted p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{s.sport}</div>
          <div className="font-display font-bold text-sm mt-0.5">{s.teams}</div>
          <div className="text-xs text-foreground mt-1">{s.details}</div>
          <div className="text-[10px] font-black uppercase mt-1 text-signal">{s.status}</div>
        </div>
      ))}
    </div>
  </div>
);

const CategoryRail = ({ title, slug, color, stories, cats }) => (
  <section className="mt-10" data-testid={`rail-${slug}`}>
    <div className="flex items-end justify-between px-4 sm:px-6 mb-4">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: color }} />
        <h2 className="font-display font-extrabold text-xl sm:text-2xl">{title}</h2>
      </div>
      <Link to={`/category/${slug}`} className="text-xs font-bold hover:underline" style={{ color }}>See all →</Link>
    </div>
    <div className="md:hidden flex gap-4 overflow-x-auto no-scrollbar px-4 pb-2">
      {stories.map(s => <div key={s.id} className="flex-none w-[75vw] max-w-[320px]"><StoryCard story={s} cats={cats} /></div>)}
    </div>
    <div className="hidden md:grid grid-cols-4 gap-5 px-6">
      {stories.map(s => <StoryCard key={s.id} story={s} cats={cats} />)}
    </div>
  </section>
);

const NewsletterStrip = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try { await api.post("/newsletter/subscribe", { email }); setStatus("ok"); setEmail(""); }
    catch { setStatus("err"); }
  };
  return (
    <section className="mt-16 mx-4 sm:mx-6 rounded-3xl bg-ink text-paper p-8 sm:p-12 overflow-hidden relative" data-testid="newsletter-strip">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-signal/30 blur-3xl" />
      <div className="relative max-w-xl">
        <div className="text-xs font-black uppercase tracking-widest text-signal">The Morning Gram</div>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl mt-2">Your daily 60-second briefing. 8 AM. Dhaka time.</h2>
        <p className="text-sm text-paper/70 mt-3">Six short stories. No clutter. No clickbait. Unsubscribe anytime.</p>
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2">
          <input type="email" required placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} data-testid="newsletter-email"
            className="flex-1 h-12 px-4 rounded-full bg-paper/10 border border-paper/20 text-paper placeholder:text-paper/40 outline-none focus:border-signal" />
          <button data-testid="newsletter-submit" className="h-12 px-6 rounded-full bg-signal text-white font-bold">
            {status === "loading" ? "..." : "Subscribe"}
          </button>
        </form>
        {status === "ok" && <p className="mt-3 text-sm text-emerald-400">You're in. First edition tomorrow at 8 AM.</p>}
        {status === "err" && <p className="mt-3 text-sm text-signal">Something broke. Try again?</p>}
      </div>
    </section>
  );
};

const FollowStrip = () => {
  const items = [
    { Icon: Instagram, label: "Instagram", url: "https://instagram.com" },
    { Icon: Facebook,  label: "Facebook",  url: "https://facebook.com" },
    { Icon: Linkedin,  label: "LinkedIn",  url: "https://linkedin.com" },
    { Icon: Twitter,   label: "X",         url: "https://x.com" },
    { Icon: Youtube,   label: "YouTube",   url: "https://youtube.com" },
    { Icon: Mail,      label: "WhatsApp",  url: "https://whatsapp.com" },
  ];
  return (
    <section className="mt-10 px-4 sm:px-6" data-testid="follow-strip">
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="font-display font-extrabold text-lg mb-3">Follow Newsgram</div>
        <div className="flex flex-wrap gap-2">
          {items.map(({ Icon, label, url }) => (
            <a key={label} href={url} target="_blank" rel="noreferrer" data-testid={`follow-${label.toLowerCase()}`}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-muted hover:bg-foreground hover:text-background text-sm font-semibold">
              <Icon className="w-4 h-4" /> {label}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { lang } = useApp();
  const [data, setData] = useState({ stories: [], cats: [], sixty: [], trending: [], explainers: [], market: {}, scores: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [stories, cats, sixty, trending, explainers, market, scores] = await Promise.all([
          api.get("/stories", { params: { limit: 50 } }).then(r => r.data),
          api.get("/categories").then(r => r.data),
          api.get("/stories", { params: { in_60_seconds: true, limit: 7 } }).then(r => r.data),
          api.get("/stories", { params: { trending: true, limit: 5 } }).then(r => r.data),
          api.get("/stories", { params: { explainer: true, limit: 4 } }).then(r => r.data),
          api.get("/market").then(r => r.data),
          api.get("/scores").then(r => r.data),
        ]);
        setData({ stories, cats, sixty, trending, explainers, market, scores });
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const lead = data.stories.find(s => s.is_featured) || data.stories[0];
  const featured = data.stories.filter(s => s.is_featured && s.id !== lead?.id).slice(0, 4);
  const byCat = (slug) => data.stories.filter(s => data.cats.find(c => c.slug === slug)?.id === s.category_id).slice(0, 4);

  return (
    <div className={lang === "bn" ? "font-bn" : ""}>
      <div className="max-w-7xl mx-auto">
        <div className="px-4 sm:px-6 pt-6">
          {lead && <LeadHero story={lead} cats={data.cats} />}
          {featured.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {featured.map(s => <StoryCard key={s.id} story={s} cats={data.cats} size="sm" />)}
            </div>
          )}
        </div>

        <SixtySecondsRail stories={data.sixty} cats={data.cats} />

        <div className="grid lg:grid-cols-[1fr_300px] gap-6 px-4 sm:px-6 mt-6">
          <div>
            <CategoryRail title="Politics" slug="politics" color="#7C3AED" stories={byCat("politics")} cats={data.cats} />
            <CategoryRail title="Business" slug="business" color="#2563EB" stories={byCat("business")} cats={data.cats} />
            <CategoryRail title="Sports" slug="sports" color="#16A34A" stories={byCat("sports")} cats={data.cats} />
            <CategoryRail title="Entertainment" slug="entertainment" color="#EC4899" stories={byCat("entertainment")} cats={data.cats} />
            <Section title="Trending now" kicker="Most viewed in 24h">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-4 sm:px-6">
                {data.trending.map(s => <StoryCard key={s.id} story={s} cats={data.cats} />)}
              </div>
            </Section>
            <Section title="Newsgram Explains" kicker="Deeper, calmer" link="/explainers">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-4 sm:px-6">
                {data.explainers.map(s => <StoryCard key={s.id} story={s} cats={data.cats} />)}
              </div>
            </Section>
            <CategoryRail title="Bangladesh" slug="bangladesh" color="#F59E0B" stories={byCat("bangladesh")} cats={data.cats} />
            <CategoryRail title="World" slug="world" color="#14B8A6" stories={byCat("world")} cats={data.cats} />
            <CategoryRail title="Tech" slug="tech" color="#0EA5E9" stories={byCat("tech")} cats={data.cats} />
          </div>
          <aside className="space-y-5 lg:sticky lg:top-24 self-start">
            <MarketWidget market={data.market} />
            <ScoresWidget scores={data.scores} />
          </aside>
        </div>

        <NewsletterStrip />
        <FollowStrip />
      </div>
    </div>
  );
}
